import { ColumnsType } from 'antd/es/table';
import React, { FC, memo, useMemo } from 'react';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { useAppDispatch, useAppSelector } from 'app/config/storeConfig/hooks';
import { ReactComponent as FiltersIcon } from 'shared/assets/icons/FiltersIcon.svg';
import { ServerTableActions, ServerTableRowSelectionActions } from 'shared/ui/ServerTable/types';
import { useGetCurrencySymbol } from 'app/appState';
import { getBoxRentalPriceAmount, Box, useGetUnitsQuery } from 'entities/Box';
import { ServerTable } from 'shared/ui/ServerTable';
import { stepperModalActions } from 'features/StepperModal';
import { getLocalizedString } from 'shared/utils/helpers/JSONLocalization';
import { useTableFilterContext } from 'features/TableFilter';
import { getSelectedWarehouse } from '../../../model/selectors/getSelectedWarehouse';
import { sendOfferModalActions } from '../../../model/slice/sendOfferModalSlice';
import { BoxesListFilter } from './BoxesListFilter';
import { transformBoxesListFilters } from './utils/transformBoxesListFilters';
import { getSizeCodeTypeString } from 'entities/SizeCode';
import { roundNumber } from 'shared/utils/helpers/roundNumber';
import { getSelectedUserId } from '../../../model/selectors/getSelectedUserId';
import { useGetContractsByUserIdQuery } from 'entities/Contract';
import { useGetUserReservationsQuery } from 'entities/User';
import { LoyaltyUserSetting } from 'entities/RentOption';

export const SelectUnitsStep: FC = memo(() => {
  const { t } = useAppTranslation(['contracts', 'booking', 'boxes']);

  const dispatch = useAppDispatch();

  const selectedWarehouse = useAppSelector(getSelectedWarehouse);
  const selectedUserId = useAppSelector(getSelectedUserId);

  const { data: userContracts } = useGetContractsByUserIdQuery({ userId: selectedUserId }, { skip: !selectedUserId });
  const { data: userReservations } = useGetUserReservationsQuery({ userId: selectedUserId, isTakenUp: true }, { skip: !selectedUserId });

  const isNewCustomer = useMemo<boolean>(() => {
    return !userContracts?.length && !userReservations?.length;
  }, [userContracts?.length, userReservations?.length]);

  const { setFiltersOpened, appliedFilters, tags, clearAllFilters } = useTableFilterContext();

  const currencySymbol = useGetCurrencySymbol();

  const tableActions: Array<ServerTableActions<Box>> = useMemo(
    () => [
      {
        name: t('Filters', { ns: 'boxes' }),
        icon: <FiltersIcon />,
        theme: 'secondary',
        iconPosition: 'prev',
        onClick: () => {
          setFiltersOpened(true);
        },
      },
    ],
    [setFiltersOpened, t],
  );

  const columns = useMemo<ColumnsType<Box>>(
    () => [
      {
        title: t('Unit'),
        key: 'name',
        render: (_, record) => record.name,
      },
      {
        title: <span className="flex justify-center">{t('Size')}</span>,
        key: 'sizeCode.square',
        sorter: true,
        width: '30%',
        sortDirections: ['ascend', 'descend', 'ascend'],
        render: (_, record) => {
          const size = `${roundNumber(record?.height)}m (${t('H', { ns: 'booking' })}) x ${roundNumber(record?.width)}m (${t('W', {
            ns: 'booking',
          })}) x ${roundNumber(record?.length)}m (${t('D', { ns: 'booking' })})`;

          const doorWidthString = `${t('Door width', { ns: 'boxes' })} - ${roundNumber(record?.doorWidth)}m`;

          return (
            <div className="flex flex-col">
              <span className="text-center">
                {record.sizeCode.square} {getSizeCodeTypeString(record.sizeCode.sizeCodeType)}
              </span>
              <span className="text-xs text-center">{size}</span>
              {record?.doorWidth && <span className="text-xs text-center">{doorWidthString}</span>}
            </div>
          );
        },
      },
      {
        title: t('Location'),
        key: 'floor.floorId',
        sorter: true,
        sortDirections: ['ascend', 'descend', 'ascend'],
        render: (_, record) => `${getLocalizedString(record.floor?.name) || ''}`,
      },
      {
        title: t('Price'),
        key: 'price',
        render: (_, record) => {
          const price = getBoxRentalPriceAmount(record);

          return price ? `${price} ${currencySymbol}` : '';
        },
      },
      {
        title: t('Promotions'),
        key: 'rentOptions',
        render: (_, record) => {
          const filteredRentOptionsByLoyaltySetting = record.rentOptions?.filter((rentOption) => {
            if (isNewCustomer) {
              return [LoyaltyUserSetting.ALL, LoyaltyUserSetting.ONLY_NEW].includes(rentOption.loyaltyUserSetting);
            }

            return [LoyaltyUserSetting.ALL, LoyaltyUserSetting.ONLY_OLD].includes(rentOption.loyaltyUserSetting);
          });

          return filteredRentOptionsByLoyaltySetting?.map((option, index) => (
            <div key={index} className="text-primaryLight font-normal">
              {index + 1}. {getLocalizedString(option.label)}
            </div>
          ));
        },
      },
    ],
    [currencySymbol, isNewCustomer, t],
  );

  const rowSelectionActions: Array<ServerTableRowSelectionActions<Box>> = [
    {
      name: t('Next'),
      onClick: (selectedElements) => {
        dispatch(sendOfferModalActions.setSelectedBoxes(selectedElements));
        dispatch(stepperModalActions.changeActiveStepIndex(3));
      },
    },
  ];

  return (
    <>
      <ServerTable
        columns={columns}
        rowKey="boxId"
        search
        title={t('Select Units')}
        selectionType="checkbox"
        rowSelectionActions={rowSelectionActions}
        fetchData={useGetUnitsQuery}
        onAllFiltersClear={clearAllFilters}
        defaultFilters={{
          warehouseId: selectedWarehouse?.warehouseId,
          availableRentOptions: true,
          ...transformBoxesListFilters(appliedFilters),
        }}
        tags={tags}
        tableActions={tableActions}
      />
      <BoxesListFilter />
    </>
  );
});
